import { Injectable } from '@angular/core';
import {catchError, Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {GeneralService} from './general.service';
import {map} from "rxjs/operators";
import {AACBusinessUnit, AACSubscription} from "../models/AAC_BusinessUnit.model";
import {DateTime} from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class AACDataService {

  constructor(
      private _httpClient: HttpClient,
      public genHttp: GeneralService,
  ) {  }

    public getRegisteredUser(username: string): Observable<any> {
        const URL = 'licenseURL/registeredUsers/username/' + username + '/';
        return this.genHttp.icGet(URL).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public validateRegistration(businessUnit: string, username: string, agentId: number): Observable<any> {
        const URL = 'licenseURL/validateRegistration/businessUnit/' + businessUnit + '/user/' + username + '/agentId/' + agentId;
        return this.genHttp.icGet(URL).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public registerBusinessUnit(businessUnit: number,
                                businessUnitName: string,
                                businessUnitType: string,
                                businessUnitDomain: string,
                                clusterName: string,
                                username: string): Observable<any> {
        const data = JSON.stringify({businessUnit: businessUnit,
            businessUnitName: businessUnitName,
            businessUnitType: businessUnitType,
            businessUnitDomain: businessUnitDomain,
            clusterName: clusterName,
            maxActiveUsers: 5,
            serviceLevel: 'TRIAL',
            isApprovalRequired: 'N',
            trialDays: 90,
            isActive: 'Y',
            insUser: username});
        const URL = 'licenseURL/registeredBusinessUnit/';
        return this.genHttp.icPost(URL, data).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public registerUser(agentId: number,
                        agentFirstName: string,
                        agentLastName: string,
                        username: string,
                        email: string,
                        businessUnitId: number,
                        emailOptIn: string,
                        acknowledgedTerms: string,
                        isApproved: string,
                        businessUnit: number): Observable<any> {
        const data = JSON.stringify({agentId: agentId,
            agentFirstName: agentFirstName,
            agentLastName: agentLastName,
            username: username,
            email: email,
            businessUnitId: businessUnitId,
            businessUnit: businessUnit,
            emailOptIn: emailOptIn,
            acknowledgedTerms: acknowledgedTerms,
            isApproved: isApproved,
            isActive: 'Y',
            insUser: username});
        const URL = 'licenseURL/registeredUser/';
        return this.genHttp.icPost(URL, data).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public updateRegisterBusinessUnit(businessUnit: AACBusinessUnit,
                                      modUser: string): Observable<any> {
        const data = JSON.stringify({businessUnit: businessUnit.businessUnit,
            businessUnitName: businessUnit.businessUnitName,
            businessUnitType: businessUnit.businessUnitType,
            businessUnitDomain: businessUnit.businessUnitDomain,
            clusterName: businessUnit.clusterName,
            serviceLevel: businessUnit.serviceLevel,
            billingId: businessUnit.billingId,
            maxActiveUsers: businessUnit.maxActiveUsers,
            trialDays: businessUnit.trialDays,
            isActive: businessUnit.isActive ? 'Y' : 'N',
            modUser: modUser});

        const URL = 'licenseURL/registeredBusinessUnit/' + businessUnit.id;
        return this.genHttp.icPost(URL, data).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public activateRegisteredUser(agentId: number,
                        agentFirstName: string,
                        agentLastName: string,
                        username: string,
                        email: string,
                        businessUnit: number,
                        modUser: string): Observable<any> {
        const data = JSON.stringify({agentId: agentId,
            agentFirstName: agentFirstName,
            agentLastName: agentLastName,
            username: username,
            email: email,
            businessUnit: businessUnit,
            modUser: modUser});
        const URL = 'licenseURL/registeredUser/activate';
        return this.genHttp.icPost(URL, data).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public deactivateRegisteredUser(agentId: number,
                                agentFirstName: string,
                                agentLastName: string,
                                username: string,
                                email: string,
                                businessUnit: number,
                                modUser: string): Observable<any> {
        const data = JSON.stringify({agentId: agentId,
            agentFirstName: agentFirstName,
            agentLastName: agentLastName,
            username: username,
            email: email,
            businessUnit: businessUnit,
            modUser: modUser});
        const URL = 'licenseURL/registeredUser/deactivate';
        return this.genHttp.icPost(URL, data).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public getAACBusinessUnits(): Promise<AACBusinessUnit[]> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('licenseURL/registeredBusinessUnits')
                .pipe(map((response: any) => {
                    let businessUnitsList = null;
                    if (response.success) {
                        businessUnitsList = response.businessUnits.map(data => new AACBusinessUnit(data));
                    }
                    return businessUnitsList;
                }))
                .subscribe((response: AACBusinessUnit[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getAACBusinessUnit(businessUnit: number): Promise<AACBusinessUnit> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('licenseURL/registeredBusinessUnit/' + businessUnit)
                .pipe(map((response: any) => {
                    let businessUnit = null;
                    if (response.success) {
                        businessUnit = new AACBusinessUnit(response.aacBusinessUnit,response.registeredUsers);
                    }
                    return businessUnit;
                }))
                .subscribe((response: AACBusinessUnit) => {
                    resolve(response);
                }, reject);
        });
    }

    public createSubscription(subscription: AACSubscription,
                                username: string): Observable<any> {
        // format SQL Date 2024-08-22 09:18:18
        let expireDate = DateTime.fromJSDate(subscription.expirationDate).toFormat('yyyy-MM-dd');
        expireDate += ' 12:00:00';

        let renewalDate = DateTime.fromJSDate(subscription.renewalDate).toFormat('yyyy-MM-dd');
        renewalDate += ' 12:00:00';

        const data = JSON.stringify({subscriptionId: subscription.subscriptionId,
            description: subscription.description,
            subscriptionType: subscription.subscriptionType,
            expirationDate: expireDate,
            renewalDate: renewalDate,
            isActive: subscription.isActive ? 'Y' : 'N',
            insUser: username});
        const URL = 'licenseURL/subscription';
        return this.genHttp.icPost(URL, data).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }

    public updateSubscription(subscription: AACSubscription,
                              username: string): Observable<any> {
        // format SQL Date 2024-08-22 09:18:18
        let expireDate = DateTime.fromJSDate(subscription.expirationDate).toFormat('yyyy-MM-dd');
        expireDate += ' 12:00:00';

        let renewalDate = DateTime.fromJSDate(subscription.renewalDate).toFormat('yyyy-MM-dd');
        renewalDate += ' 12:00:00';

        const data = JSON.stringify({subscriptionId: subscription.subscriptionId,
            description: subscription.description,
            subscriptionType: subscription.subscriptionType,
            expirationDate: expireDate,
            renewalDate: renewalDate,
            isActive: subscription.isActive ? 'Y' : 'N',
            modUser: username});
        const URL = 'licenseURL/subscription/' + subscription.id;
        return this.genHttp.icPost(URL, data).pipe(
            catchError(err => of({isError: true, error: err})),
        );
    }
}
